import { IFormWidgetStyleData, IWidgetWithFormData } from "@pageBuilder/typings";
import { FormFieldRenderer } from "./form-field-renderer";
import { FormStyleRenderer } from "./form-style-renderer";
import { WidgetRendererBase } from "../widget-renderer-base";
import { FormCaptchaRenderer } from "./form-captcha-renderer";
import { IRenderingCommonContext } from "@commonRendering/types";

export abstract class FormWidgetBaseRenderer extends WidgetRendererBase<IWidgetWithFormData> {

    private readonly styleRenderer: FormStyleRenderer;
    private readonly captchaRenderer = new FormCaptchaRenderer();
    private readonly MOBILE_WIDTH = 479;

    protected constructor(renderingCommonContext: IRenderingCommonContext) {
        super(renderingCommonContext);
        this.styleRenderer = new FormStyleRenderer();
        this.captchaRenderer = new FormCaptchaRenderer();
    }

    protected renderInternal(element: HTMLElement, data: IWidgetWithFormData): HTMLElement {
        return this.renderFormWidgetInternal(element, data);
    }

    protected renderStyles(element: HTMLElement, data: IWidgetWithFormData): void {
        this.renderBaseStyles(element, data.style);
    }

    private renderBaseStyles(element: HTMLElement, style: IFormWidgetStyleData): void {
        this.animationRenderer.setAnimationStyles(element, style.animation);
    }

    protected renderBaseInternal(element: HTMLElement, data: IWidgetWithFormData, skipStyles: boolean = false): HTMLElement {
        this.cleanupInternal(element);
        const form = this.ensureFormElement(element);

        this.setFormData(form, data);

        form.querySelectorAll(".fields-wrapper").forEach(x => x.remove());
        form.querySelectorAll(".labels-wrapper").forEach(x => x.remove());

        const labelsStyle = data.style?.labels?.style ?? "standard";
        form.setAttribute("data-labels-style", labelsStyle);

        const isMobile = element.clientWidth <= this.MOBILE_WIDTH;

        if (data.style?.layout === "row" && !isMobile && data.style.labels?.style === "standard") {
            form?.appendChild(FormFieldRenderer.createLabels(data.fields));
        }

        const fieldsWrapper = FormFieldRenderer.createFieldsWrapper();
        data.fields.forEach(x => {
            // Need to change id of Contact field with name PhoneNumber,
            // because contact fields requested from CrmNext by names and CRM renamed this field to MobilePhone.
            if (x.id === "PhoneNumber") {
                x.id = "MobilePhone";
            }

            const field = FormFieldRenderer.createField(x, data.style.layout, labelsStyle, isMobile);
            fieldsWrapper?.appendChild(field);
        });

        FormFieldRenderer.createActions(form, fieldsWrapper, data.submit, data.style?.submit?.theme ?? "primary");
        form?.appendChild(fieldsWrapper);

        const textAreaFieldExist = data.fields.find(field => field.type === "TextArea");

        form.classList.toggle("row-layout", data.style.layout === "row");
        form.classList.toggle("column-layout", data.style.layout === "column");
        form.style.setProperty("--fields-number", data.fields.length.toString());
        form.style.setProperty("--action-height", data.style.layout === "row" && textAreaFieldExist ? "80" : "40");

        if (!skipStyles) {
            const stylesProperties = this.styleRenderer.getStyles(data.style);
            this.styleService.setStyle(element, `#${element.id}`, stylesProperties);
        }

        this.animationRenderer.setAnimationStyles(element, data.style.animation);

        this.renderCaptcha(element, data.disableReCaptcha);
        return element;
    }

    protected renderCaptcha(element: HTMLElement, disableReCaptcha: boolean | undefined): void {
        if (this.isEditorRendering || this.isPreviewRendering || disableReCaptcha) {
            return;
        }

        this.captchaRenderer.render(element, this.renderingCommonContext.data.captchaInfo);
    }

    protected ensureFormElement(element: HTMLElement): HTMLFormElement {
        const content = element.querySelector(".widget-content") as HTMLElement;
        let form = content.querySelector("form");
        if (!form) {
            form = this.createForm();
            content.appendChild(form);
        }

        return form;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected cleanupInternal(element: HTMLElement): void {
        //nothing
    }

    protected getContentSelectorsInternal(element: HTMLElement): Array<string> {
        return [`#${element.id}`];
    }

    protected abstract createForm(): HTMLFormElement;

    protected abstract setFormData(form: HTMLFormElement, data: IWidgetWithFormData): void;

    protected abstract renderFormWidgetInternal(element: HTMLElement, data: IWidgetWithFormData): HTMLElement;

    protected createBaseForm(): HTMLFormElement {
        const form = document.createElement("form");
        form.classList.add("tv-form");
        form.method = "POST";
        form.enctype = "multipart/form-data";
        return form;
    }

    protected setBaseFormData(form: HTMLFormElement, data: IWidgetWithFormData): void {
        FormFieldRenderer.ensureHidden(form, "__honey__pot_", "");
        if (data.fileUploadLimit) {
            FormFieldRenderer.ensureHidden(form, "__fileupload__limit_", data.fileUploadLimit.toString());
        }

        if (data.addTags) {
            FormFieldRenderer.ensureHidden(form, "__tags_add_", JSON.stringify(data.addTags));
        } else {
            this.clearTags(form, "__tags_add_");
        }

        if (data.removeTags) {
            FormFieldRenderer.ensureHidden(form, "__tags_remove_", JSON.stringify(data.removeTags));
        } else {
            this.clearTags(form, "__tags_remove_");
        }

        if (data.disableReCaptcha) {
            FormFieldRenderer.ensureHidden(form, "__disable_re_captcha_", data.disableReCaptcha.toString());
        } else {
            this.clearTags(form, "__disable_re_captcha_");
        }

        if (data.signature) {
            FormFieldRenderer.ensureHidden(form, "__tv_signature", data.signature);
        } else {
            this.clearTags(form, "__tv_signature");
        }

    }

    private clearTags(form: HTMLElement, name: string): void {
        const tags = form.querySelectorAll<HTMLInputElement>(`[type=hidden][name=${name}]`);
        tags.forEach(x => x.removeAttribute("value"));
    }

}
